import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { Card } from '../../components/Card';
import { CmsEditorLayout } from '../../components/CmsEditorLayout';
import { useResellerCms, saveResellerCmsSection, clearResellerCmsSection, type AdminCmsSection } from '../../api/cms';
import { CMS_REGISTRY } from '../../content/registry';
import { LIST_FALLBACKS, RESELLER_LIST_OVERRIDES } from '../../content/fallbacks';

/**
 * Reseller marketing-content editor — mirrors the platform-admin Cms.tsx
 * editor (same shared two-pane CmsEditorLayout), registry-driven and filtered
 * to the sections flagged `resellerEditable`: the reseller's own homepage
 * (Landing.tsx), pricing page, and contact page. A reseller may have no row
 * yet for a section — it then shows the built-in default copy as a
 * placeholder hint (or, for a repeatable list, pre-filled starting content)
 * and a "Using default copy" badge; saving writes only that reseller's own
 * scope (RLS-enforced on the API), and "Reset to default" removes the
 * override.
 */
export function Content() {
  const { data: sections = [], isLoading } = useResellerCms();
  const qc = useQueryClient();
  const byKey = new Map<string, AdminCmsSection>(sections.map((s) => [s.sectionKey, s]));
  const pages = CMS_REGISTRY.map((page) => ({ ...page, sections: page.sections.filter((s) => s.resellerEditable) })).filter(
    (page) => page.sections.length > 0,
  );

  async function onSaved() {
    await qc.invalidateQueries({ queryKey: ['reseller-cms'] });
    await qc.invalidateQueries({ queryKey: ['cms'] });
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-5xl mx-auto">
      <PageHeader
        eyebrow="Brand"
        title="Content"
        subtitle="Edit the copy on your own homepage, pricing page, and contact page. Leave a field blank to keep the default copy."
      />
      {isLoading ? (
        <Card>
          <div className="p-6 space-y-3" aria-busy="true">
            <div className="skeleton h-5 w-40 rounded" />
            <div className="skeleton h-24 rounded" />
          </div>
        </Card>
      ) : (
        <CmsEditorLayout
          pages={pages}
          byKey={byKey}
          fallbackListFor={(key) => RESELLER_LIST_OVERRIDES[key] ?? LIST_FALLBACKS[key] ?? []}
          save={saveResellerCmsSection}
          clear={clearResellerCmsSection}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
